import { useEffect, useState } from "react";
import {
  Inbox, Send, Loader2, Check, X, Clock, CheckCircle, XCircle, CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COL = {
  ink: "#1A1A1A", sub: "#4A4A4A", line: "#0000000D",
  green: "#2D6A4F", greenSoft: "#2D6A4F12",
  orange: "#E07A5F", gold: "#D4A373",
};

const STATUS_LABEL = {
  pending: { label: "En attente", color: COL.gold, icon: Clock },
  accepted: { label: "Acceptée", color: COL.green, icon: CheckCircle },
  rejected: { label: "Refusée", color: "#B23A48", icon: XCircle },
  cancelled: { label: "Annulée", color: COL.sub, icon: X },
  payment_pending: { label: "Paiement en attente", color: COL.orange, icon: CreditCard },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABEL[status] || { label: status, color: COL.sub, icon: Clock };
  const Icon = cfg.icon;
  return (
    <Badge className="text-[9px] gap-1 px-2" style={{ background: cfg.color + "20", color: cfg.color, border: "none" }}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </Badge>
  );
};

const RequestCard = ({ request, role, onAction }) => {
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${request.request_id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Erreur");
      } else if (data.status === "accepted") {
        toast.success("Demande acceptée — accès accordé gratuitement");
        onAction?.();
      } else if (data.status === "payment_required" && data.checkout_url) {
        toast.info("Limite de collaborateurs gratuits atteinte.");
        setTimeout(() => { window.location.href = data.checkout_url; }, 800);
      }
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  const reject = async () => {
    const reason = window.prompt("Raison du refus (optionnel) :", "") || null;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${request.request_id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        toast.success("Demande refusée");
        onAction?.();
      }
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  const cancel = async () => {
    if (!window.confirm("Annuler cette demande ?")) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/access-requests/${request.request_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Demande annulée");
        onAction?.();
      }
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  const isOwner = role === "received";
  const otherParty = isOwner ? request.requester_email : request.owner_email;
  const date = request.created_at ? new Date(request.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div
      className="p-4 rounded-2xl border bg-white"
      style={{ borderColor: COL.line }}
      data-testid={`request-card-${request.request_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: COL.ink }}>
            {request.parcel_name || request.parcel_id}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: COL.sub }}>
            <span className="font-mono">{request.parcel_id}</span>
            {request.parcel_commune ? ` · ${request.parcel_commune}` : ""}
          </p>
        </div>
        <StatusBadge status={request.status} />
      </div>

      <div className="text-[11px] space-y-1 mb-3" style={{ color: COL.sub }}>
        <p>
          <span className="font-medium">{isOwner ? "Demandeur" : "Propriétaire"}</span> :{" "}
          <span style={{ color: COL.ink }}>{otherParty}</span>
        </p>
        {request.message && (
          <p className="italic p-2 rounded-lg mt-1" style={{ background: "#F9F8F6", color: COL.ink }}>
            « {request.message} »
          </p>
        )}
        <p>Envoyée le {date}</p>
        {request.denial_reason && (
          <p style={{ color: "#B23A48" }}>Motif du refus : {request.denial_reason}</p>
        )}
      </div>

      {request.status === "pending" && isOwner && (
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 h-8 text-[11px] font-semibold rounded-lg text-white gap-1"
            style={{ background: COL.green }}
            onClick={accept}
            disabled={busy}
            data-testid={`accept-${request.request_id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Accepter
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-[11px] rounded-lg gap-1"
            style={{ color: "#B23A48", borderColor: "#B23A4830" }}
            onClick={reject}
            disabled={busy}
            data-testid={`reject-${request.request_id}`}
          >
            <X className="w-3.5 h-3.5" /> Refuser
          </Button>
        </div>
      )}

      {request.status === "pending" && !isOwner && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-[10px] text-red-600 hover:bg-red-50 px-2"
          onClick={cancel}
          disabled={busy}
          data-testid={`cancel-${request.request_id}`}
        >
          Annuler ma demande
        </Button>
      )}
    </div>
  );
};

export default function AccessRequestsSection() {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [tab, setTab] = useState("received");
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        authFetch(`${API_BASE}/access-requests/received`).then(r => r.ok ? r.json() : []),
        authFetch(`${API_BASE}/access-requests/sent`).then(r => r.ok ? r.json() : []),
      ]);
      setReceived(r);
      setSent(s);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const receivedPending = received.filter(r => r.status === "pending").length;
  const sentPending = sent.filter(r => r.status === "pending").length;

  return (
    <section data-testid="section-access-requests" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COL.ink, fontFamily: "Manrope, sans-serif" }}>
            Demandes d'accès
          </h2>
          <p className="text-sm mt-0.5" style={{ color: COL.sub }}>
            Gérez les demandes envoyées et reçues pour ouvrir les Carnets PRO.
          </p>
        </div>
        <div className="flex gap-1.5 p-1 rounded-full border bg-white" style={{ borderColor: COL.line }}>
          <button
            onClick={() => setTab("received")}
            data-testid="tab-received"
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${tab === "received" ? "text-white" : ""}`}
            style={tab === "received" ? { background: COL.green } : { color: COL.sub }}
          >
            <Inbox className="w-3 h-3 inline mr-1" />
            Reçues {receivedPending > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px]" style={{ background: tab === "received" ? "rgba(255,255,255,0.25)" : COL.greenSoft }}>{receivedPending}</span>}
          </button>
          <button
            onClick={() => setTab("sent")}
            data-testid="tab-sent"
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${tab === "sent" ? "text-white" : ""}`}
            style={tab === "sent" ? { background: COL.orange } : { color: COL.sub }}
          >
            <Send className="w-3 h-3 inline mr-1" />
            Envoyées {sentPending > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px]" style={{ background: tab === "sent" ? "rgba(255,255,255,0.25)" : "#E07A5F18" }}>{sentPending}</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-6">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: COL.green }} />
        </div>
      ) : (
        <>
          {tab === "received" && (
            received.length === 0 ? (
              <div className="p-8 rounded-2xl border-2 border-dashed text-center" style={{ borderColor: COL.line }} data-testid="received-empty">
                <Inbox className="w-8 h-8 mx-auto mb-3" style={{ color: COL.sub }} />
                <p className="text-sm" style={{ color: COL.sub }}>
                  Aucune demande d'accès reçue.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {received.map(r => <RequestCard key={r.request_id} request={r} role="received" onAction={fetchAll} />)}
              </div>
            )
          )}
          {tab === "sent" && (
            sent.length === 0 ? (
              <div className="p-8 rounded-2xl border-2 border-dashed text-center" style={{ borderColor: COL.line }} data-testid="sent-empty">
                <Send className="w-8 h-8 mx-auto mb-3" style={{ color: COL.sub }} />
                <p className="text-sm" style={{ color: COL.sub }}>
                  Aucune demande envoyée. Verrouillez ou demandez l'accès depuis la carte.
                </p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {sent.map(r => <RequestCard key={r.request_id} request={r} role="sent" onAction={fetchAll} />)}
              </div>
            )
          )}
        </>
      )}
    </section>
  );
}
